import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { InstanceApi } from '@eis/insuranceservice-sdk';
import { useApiClients } from './ApiClientsProvider';
import { apiKeys } from './keys';
import { isSet } from './list-query';
import { unwrap } from './result';

/**
 * PREMIUMS (SDD-021 §7.4, §8.1): the three reads the console's premium sections ask.
 *
 * Every figure here is the SERVICE's — net, tax per code and gross as decimal strings — and the
 * console never adds them up (prompt 29 Decision 9): a total on screen is a total the service
 * computed, with the same `pkg/premium` code the collective invoice runs.
 *
 * Two settings are shared and deliberate:
 *  - `staleTime` 30 s. An estimate is a fresh calculation through the premium engine; reopening a
 *    tab a few seconds later should not ask the engine again.
 *  - `retry: false`. A failing engine is ANSWERED (an estimate carries `error`, the request
 *    succeeds) or refused once (503 PREMIUM_ENGINE_UNAVAILABLE); either way the screen shows it,
 *    and a retry loop against an engine that is down only makes it slower to say so.
 */
export const PREMIUM_STALE_TIME = 30_000;

/**
 * What the summary is about: one business day (`validAt`, the members active that day, every
 * amount a monthly equivalent), or a half-open period (`periodFrom` inclusive, `periodTo`
 * exclusive; every member covered at any time in it, pro-rated). Never both — the service refuses
 * that with `VALID_AT_AND_PERIOD_EXCLUSIVE`. An empty `validAt` is today, the service's default.
 */
export type PremiumSummaryBasis =
  { readonly validAt?: string } | { readonly periodFrom: string; readonly periodTo: string };

type SummaryQuery = NonNullable<
  InstanceApi.operations['GroupPolicyService_GetGroupPolicyPremiumSummary']['parameters']['query']
>;

/** True for the period form of a basis. */
export const isPeriodBasis = (
  basis: PremiumSummaryBasis,
): basis is { readonly periodFrom: string; readonly periodTo: string } =>
  'periodFrom' in basis && 'periodTo' in basis;

/** The request a basis means — only what says something is sent. */
export function premiumSummaryQuery(basis: PremiumSummaryBasis): SummaryQuery {
  if (isPeriodBasis(basis)) return { periodFrom: basis.periodFrom, periodTo: basis.periodTo };
  return isSet(basis.validAt) ? { validAt: basis.validAt } : {};
}

/**
 * `GET /v1/group-policies/{code}/premium-summary` — the server-side totals over ALL members (never
 * the loaded rows): the member count, the members' net, tax per code and gross, every aggregation
 * adjustment (applied or not, with its reason), the totals and the one-time premiums.
 */
export function useGroupPolicyPremiumSummary(
  code: string,
  basis: PremiumSummaryBasis = {},
  options: { readonly enabled?: boolean } = {},
): UseQueryResult<InstanceApi.GetGroupPolicyPremiumSummaryResponse> {
  const { instance } = useApiClients();
  const query = premiumSummaryQuery(basis);
  return useQuery({
    queryKey: apiKeys.instance.groupPolicyPremiumSummary(code, { ...query }),
    queryFn: () =>
      unwrap(
        instance.client.GET('/v1/group-policies/{code}/premium-summary', {
          params: { path: { code }, query },
        }),
      ),
    enabled: (options.enabled ?? true) && code !== '',
    staleTime: PREMIUM_STALE_TIME,
    retry: false,
  });
}

/**
 * `GET /v1/policies/{code}/premium-estimate` — the premium booked at `validAt` (today when absent)
 * beside what the NEXT write would book now, with the difference and why. A failing engine is an
 * answer here (`estimate.error`), never a thrown request.
 */
export function usePolicyPremiumEstimate(
  code: string,
  validAt?: string,
  options: { readonly enabled?: boolean } = {},
): UseQueryResult<InstanceApi.EstimatePolicyPremiumResponse> {
  const { instance } = useApiClients();
  return useQuery({
    queryKey: apiKeys.instance.policyPremiumEstimate(code, isSet(validAt) ? validAt : undefined),
    queryFn: () =>
      unwrap(
        instance.client.GET('/v1/policies/{code}/premium-estimate', {
          params: { path: { code }, ...(isSet(validAt) ? { query: { validAt } } : {}) },
        }),
      ),
    enabled: (options.enabled ?? true) && code !== '',
    staleTime: PREMIUM_STALE_TIME,
    retry: false,
  });
}

/**
 * `GET /v1/leads/{code}/premium-estimate` — the draft's priced premium beside the refresh preview
 * (on a stale pin: the refreshed version's price, reason VERSION_CHANGED — the numbers the accept
 * dialog shows).
 */
export function useLeadPremiumEstimate(
  code: string,
  options: { readonly enabled?: boolean } = {},
): UseQueryResult<InstanceApi.EstimateLeadPremiumResponse> {
  const { instance } = useApiClients();
  return useQuery({
    queryKey: apiKeys.instance.leadPremiumEstimate(code),
    queryFn: () =>
      unwrap(
        instance.client.GET('/v1/leads/{code}/premium-estimate', { params: { path: { code } } }),
      ),
    enabled: (options.enabled ?? true) && code !== '',
    staleTime: PREMIUM_STALE_TIME,
    retry: false,
  });
}
